import { RETURN_OFFER } from 'analytics/constants/ids';
import {
    cancelFlow_cancelOfferClick,
    cancelFlow_cancelOfferScreenLoad,
    cancelFlow_cancelResultClick,
    cancelFlow_offerResultClick,
    cancelFlow_offerResultScreenLoad,
    cancelFlow_reasonsContinueClick,
    cancelFlow_reasonsScreenLoad,
    OfferTypes,
} from 'analytics/trackers/cancelFlow';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isUserFromCRM } from 'store/auth/selectors';
import { ModalName } from 'store/modals/types';
import { returnSubscription, sendFeedback } from 'store/subscriptions/actions';
import { getAppropriateDiscountForSubscription } from 'store/subscriptions/helpers';
import { selectDiscountedSubscriptions } from 'store/subscriptions/selectors';
import { selectIsUserFromCalifornia } from 'store/user/selectors';

import { PATHS } from 'constants/routerPaths';
import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { useTrackAmplitudeEvent } from 'services/Analytics/useAmplitude';

import { useDispatch, useSelector } from 'hooks';

import { getCurrencySign, getDiscount, getPriceForCurrency } from 'helpers/prices';
import { isMainSubscription } from 'helpers/subscriptions';
import { isEmpty } from 'helpers/utils';

import { IUserFeedback } from 'components/Modals/CancellationReasonModal/types';

import { Product } from 'types/product';
import {
    IDiscountsForSubscriptions,
    IDiscountSubscription,
    IDiscountSubscriptionInfo,
    ISubscription,
} from 'types/subscription';

import { useModals } from '../modals';

import { checkIsProductIdWithTestPrice } from './helpers';

const FEE_CHARGE_ERROR = "Can't bill the fee for subscription";

const useCommonSubscriptionLogic = (subs?: ISubscription[]) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { openModal, closeModal } = useModals();

    const mainSubscription = subs?.find((i) => isMainSubscription(i.product_type));
    const externalId = mainSubscription?.external_id;
    const trackAmplitudeEvent = useTrackAmplitudeEvent();

    const specialOfferProductRef = useRef<IDiscountsForSubscriptions>({});

    const discountedSubscriptions: IDiscountsForSubscriptions = useSelector(selectDiscountedSubscriptions);
    const isCaliforniaFlow = useSelector(selectIsUserFromCalifornia);
    const isFromCRM: boolean = useSelector(isUserFromCRM);

    useEffect(() => {
        specialOfferProductRef.current = discountedSubscriptions;
    }, [discountedSubscriptions]);

    const displaySurveyModal = (onSubmit: ({ reason }: IUserFeedback) => void, onCancel: () => void, flow: string) => {
        openModal(ModalName.cancellationReasonModal, {
            trackLoad: () => {
                cancelFlow_reasonsScreenLoad({ subscription_id: externalId! });
            },
            onClose: () => {
                closeModal();

                cancelFlow_reasonsContinueClick({ action: 'close', subscription_id: externalId });
                trackAmplitudeEvent('cancel_subscription_reason__click');
            },
            onCancel: (reason: IUserFeedback) => {
                onCancel();

                if (reason?.reason) {
                    dispatch(sendFeedback({ reason: reason.reason.shortKey }));
                }

                cancelFlow_reasonsContinueClick({
                    action: 'cancel',
                    subscription_id: externalId,
                    reason: reason.reason?.shortKey,
                });
                trackAmplitudeEvent('cancel_subscription_reason__click', { reason: reason.reason?.shortKey });
            },
            onSubmit: (reason: IUserFeedback) => {
                onSubmit(reason);

                cancelFlow_reasonsContinueClick({
                    action: 'continue',
                    subscription_id: externalId,
                    reason: reason.reason?.shortKey,
                });
                trackAmplitudeEvent('cancel_subscription_reason__click', { reason: reason.reason?.shortKey });
            },
            captions: {
                headerTitle: isCaliforniaFlow ? t('basics.feedback') : t('subscription.text.cancel'),
                ...(flow === RETURN_OFFER && {
                    additionalInfo: {
                        title: t('basics.subscription.cancelledTitle'),
                        text: t('subscription.cancellation.returnOffer.statusAdditionalInfo', {
                            minutesAmount: 15,
                        }),
                    },
                }),
                title: t('subscription.cancellationReason.title.mainReason'),
                submitButton: t('basics.continue'),
                ...(!isCaliforniaFlow && {
                    discardButton: t('subscription.cancelDialog.discard'),
                }),
            },
            isCaliforniaFlow,
        });
    };

    const handleSubmitReturnOffer = (discountedSubscriptions: IDiscountSubscription, candidate: ISubscription) => {
        displayUpdatingProcessModal();

        dispatch(
            returnSubscription({
                id: candidate?.external_id,
                product: discountedSubscriptions,
                discount_type: DISCOUNT_TYPES.extra,
                provider: candidate?.payment_provider,
                onError: () => {
                    displayProcessingIssue(
                        OfferTypes.Return_offer,
                        () => {
                            // TODO
                        },
                        () => handleSubmitReturnOffer(discountedSubscriptions, candidate)
                    );
                },
                onSuccess: () => {
                    displayOfferApplied(discountedSubscriptions, candidate.external_id);
                },
            })
        );
    };

    const handleDiscountAppliedPopupClosed = () => {
        cancelFlow_offerResultClick({ action: 'continue', result: 'success', offer_type: OfferTypes.Return_offer });
        closeModal();
    };

    const displayOfferApplied = (product: IDiscountSubscription, external_id: string) => {
        cancelFlow_offerResultScreenLoad({
            result: 'success',
            offer_type: OfferTypes.Return_offer,
            subscription_id: external_id,
        });

        openModal(ModalName.universalModal, {
            image: 'rising-hands',
            onClose: () => handleDiscountAppliedPopupClosed(),
            onPrimaryClick: () => handleDiscountAppliedPopupClosed(),
            withCloseButton: true,
            captions: {
                title: t('subscription.specialOffer.offerApplied.title'),
                extraText: {
                    text: t('subscription.specialOffer.offerApplied.description', {
                        currencySign: getCurrencySign(product?.currency),
                        price: getPriceForCurrency(product),
                        period: product.subscription_period,
                    }),
                    color: 'text-main',
                },
                primaryButton: t('basics.continue'),
            },
            dataLocators: {
                modal: 'cancelSubModal',
                container: 'updatingSubscriptionPlanModal',
                primaryButton: 'updatingSubscriptionPlanContinueButton',
                closeButton: 'updatingSubscriptionPlanCloseButton',
            },
        });
    };

    const displayUpdatingProcessModal = (dataLocator = 'subscriptionUpdateModalWaiting') => {
        openModal(ModalName.waitingModal, {
            captions: {
                title: t('subscription.updatingPlan.inProgress'),
            },
            dataLocatorContainer: dataLocator,
        });
    };

    const displayCancelProcessModal = (
        candidate: ISubscription,
        dataLocator = 'subscriptionCancelationModalWaiting'
    ) => {
        openModal(ModalName.waitingModal, {
            captions: {
                title: candidate?.fee?.fee_enabled ? '' : t('subscription.cancelDialog.waiting'),
                subtitle: candidate?.fee?.fee_enabled
                    ? t('subscription.cancelFlow.commitment.chargeModal.subtitle')
                    : '',
            },
            dataLocatorContainer: dataLocator,
        });
    };

    const displayCancellingIssue = (onRetry: () => void, externalId: string, errMsg?: string) => {
        openModal(ModalName.universalModal, {
            onClose: () => {
                cancelFlow_cancelResultClick({ action: 'close', result: 'fail', subscription_id: externalId });
                closeModal();
            },
            onPrimaryClick: () => {
                cancelFlow_cancelResultClick({ action: 'try_again', result: 'fail', subscription_id: externalId });
                onRetry();
            },
            onContactUsClick: () =>
                cancelFlow_cancelResultClick({
                    action: 'contact_support',
                    result: 'fail',
                    subscription_id: externalId,
                }),
            image: 'errorAttention',
            withCloseButton: true,
            withContactUsLink: true,
            captions: {
                title: t('subscription.cancelling.issueModal.title'),
                subtitle: errMsg?.includes(FEE_CHARGE_ERROR)
                    ? t('subscription.cancelFlow.commitment.chargeErrorModal.subtitle')
                    : '',
                primaryButton: t('basics.tryAgain'),
                tertiaryButton: t('basics.contactSupport'),
            },
        });
    };

    const displayProcessingIssue = (
        offerType: OfferTypes,
        trackLoad: () => void,
        retryCall: (discountedSubscriptions?: IDiscountSubscription, candidate?: ISubscription) => void
    ) => {
        openModal(ModalName.universalModal, {
            trackLoad,
            onClose: () => {
                cancelFlow_offerResultClick({
                    action: 'close',
                    result: 'fail',
                    offer_type: offerType,
                });
                closeModal();
            },
            onPrimaryClick: () => {
                cancelFlow_offerResultClick({
                    action: 'try_again',
                    result: 'fail',
                    offer_type: offerType,
                });
                closeModal();
                retryCall();
            },
            onContactUsClick: () =>
                cancelFlow_offerResultClick({
                    action: 'contact_support',
                    result: 'fail',
                    offer_type: offerType,
                    subscription_id: externalId,
                }),
            image: 'errorAttention',
            withCloseButton: true,
            withContactUsLink: true,
            captions: {
                title: t('subscription.updating.issueModal.title'),
                primaryButton: t('basics.tryAgain'),
                tertiaryButton: t('basics.contactSupport'),
            },
        });
    };

    const getDiscountSubscriptionInfo = (
        candidateForOffer: ISubscription,
        discountedSubscription: IDiscountSubscription
    ): IDiscountSubscriptionInfo => {
        const specialOfferPrice = getPriceForCurrency(discountedSubscription as IDiscountSubscription);
        const isSpecialOfferLessExpensive =
            checkIsProductIdWithTestPrice(candidateForOffer?.product.id) ||
            specialOfferPrice < candidateForOffer?.product.price;

        const discount = getDiscount(candidateForOffer?.product.price, specialOfferPrice);

        return {
            isSpecialOfferLessExpensive,
            discount,
        };
    };

    const displayReturnOffer = (candidateForReturnOffer: ISubscription, afterCancel: boolean | undefined = false) => {
        const discountedSubscriptions: IDiscountsForSubscriptions = specialOfferProductRef.current;

        if (isFromCRM && !afterCancel) {
            navigate(PATHS.RETURN_OFFER);

            return;
        }

        const discountedSubscription: IDiscountSubscription | undefined = getAppropriateDiscountForSubscription(
            candidateForReturnOffer,
            discountedSubscriptions,
            DISCOUNT_TYPES.extra
        );

        if (!discountedSubscription || isEmpty(discountedSubscription)) {
            closeModal();

            return;
        }

        const { isSpecialOfferLessExpensive, discount } = getDiscountSubscriptionInfo(
            candidateForReturnOffer,
            discountedSubscription
        );

        const isCurrentSubscriptionMain = isMainSubscription(candidateForReturnOffer.product_type);

        if (
            isSpecialOfferLessExpensive &&
            !candidateForReturnOffer.discount?.discount_granted_at &&
            !candidateForReturnOffer.is_promo_code_used
        ) {
            openModal(ModalName.discountOfferModal, {
                onClose: () => {
                    cancelFlow_cancelOfferClick({
                        action: 'close',
                        offer_type: OfferTypes.Return_offer,
                        subscription_id: candidateForReturnOffer.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Return_offer });

                    closeModal();
                },
                onSubmit: () => {
                    cancelFlow_cancelOfferClick({
                        action: 'claim_offer',
                        offer_type: OfferTypes.Return_offer,
                        subscription_id: candidateForReturnOffer.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__click', { offer_type: OfferTypes.Return_offer });

                    handleSubmitReturnOffer(
                        discountedSubscription as IDiscountSubscription,
                        candidateForReturnOffer as ISubscription
                    );
                },
                trackLoad: () => {
                    cancelFlow_cancelOfferScreenLoad({
                        offer_type: OfferTypes.Return_offer,
                        subscription_id: candidateForReturnOffer.external_id,
                    });
                    trackAmplitudeEvent('cancel_offer__screen__load');
                },
                captions: {
                    headerTitle: t('subscription.specialOffer.title'),
                    additionalInfo: {
                        title: t('basics.subscription.cancelledTitle'),
                        text: t('subscription.cancellation.returnOffer.statusAdditionalInfo', {
                            minutesAmount: 15,
                        }),
                    },
                    title: 'subscription.cancellation.returnOffer.renewNow.title',
                    subTitle: t('subscription.cancellation.returnOffer.renewNow.subtitle'),
                    firstBadgeText: t('subscription.cancellation.returnOffer.firstBadge'),
                    secondBadgeText: t('subscription.cancellation.specialOffer.withDynamicPercent.secondBadge', {
                        discount: discount,
                    }),
                    extraText: t('subscription.discountOffer.disclaimerNew'),
                    submitButton: t('subscription.discountOffer.button.changePlan'),
                    periodTitle: t('subscription.cancellation.specialOffer.namePeriodICU', {
                        period: discountedSubscription.subscription_period,
                    }),
                    period: discountedSubscription.subscription_period,
                },
                oldProduct: candidateForReturnOffer?.product as Product,
                specialOfferProduct: discountedSubscription as IDiscountSubscription,
                discount,
                isCurrentSubscriptionMain,
            });

            return;
        }
        console.warn('Return offer is not available');
        closeModal();
    };

    return {
        getDiscountSubscriptionInfo,
        displaySurveyModal,
        displayProcessingIssue,
        displayUpdatingProcessModal,
        displayOfferApplied,
        displayCancellingIssue,
        displayReturnOffer,
        handleSubmitReturnOffer,
        displayCancelProcessModal,
    };
};

export default useCommonSubscriptionLogic;
